/**
 * Roraima's tumbleweed, in the shared 120×120 LINE asset box.
 *
 * The owner's call for the passcode dots (2026-08-13): "çölde uçuşan saman
 * yuvarlak topağı, roraima sarısı ile", then "kawai olacak", then two reference
 * illustrations. The tepui keeps the Home tab, the menu strip and the wallpaper;
 * the dots get the thing that blows past it. Sakura has the same split between
 * its blossom and its petals.
 *
 * Two reasons it is the right object for a passcode dot specifically. It is
 * ROUND, so it keeps the round indicator LINE users expect — the argument that
 * settled Aurora's orb after two loose shapes were thrown out. And it is the one
 * part of this theme's world that MOVES, so unlike the tepui it can be rotated
 * per digit, which is how every other variant here keeps four dots from reading
 * as one asset repeated.
 *
 * THE STRUCTURE, taken from the owner's references (stock illustrations used as
 * a style reference only — every path here is generated, nothing is traced off
 * them). Three layers, and all three are load-bearing:
 *   1. a halo of HOOKS escaping the rim, tapering to a point outward,
 *   2. a solid round BODY disc in a deep straw,
 *   3. thin tapered WRAPS across the disc in the theme's yellow.
 * The bristle comes from the hooks, not from lumps in the silhouette. Two
 * earlier passes got this wrong in opposite directions: a botanically honest
 * tangle of thin strands read as a scribble, and a lumpy blob with fat rounded
 * straps read as a chestnut.
 *
 * The wraps bow BOTH ways on purpose. Bowed only away from the centre they all
 * hug the rim and leave a bald disc in the middle; a bit under half of them bow
 * inward instead and cross it.
 *
 * A fourth layer of paler strands was tried and dropped: at the same width as
 * the others they read as ribbons draped over the ball rather than as straw
 * catching the light.
 *
 * Generated from a hash rather than Math.random, so the four are irregular AND
 * the exported PNGs are byte-stable between capture runs.
 */

/** Deterministic 0..1 from an integer. */
function h( i: number ): number {
  const v = Math.sin( i * 12.9898 ) * 43758.5453;
  return v - Math.floor( v );
}

const CX = 60;
const CY = 60;
/** The body disc's radius. Hooks reach out to about 1.4 of it. */
export const TUMBLE_R = 34;

const f = ( v: number ) => +v.toFixed( 1 );
type P = [ number, number ];

/**
 * A tapered sliver along a quadratic spine: the shape a dry stem actually has.
 * `taper` is the half-width profile along the spine, so one builder covers both
 * a stem pointed at both ends and a hook that is fat at the base and pointed at
 * the tip.
 */
function sliver( p0: P, c: P, p1: P, halfW: number, taper: ( t: number ) => number, N = 18 ): string {
  const at = ( t: number ): P => {
    const u = 1 - t;
    return [
      u * u * p0[ 0 ] + 2 * u * t * c[ 0 ] + t * t * p1[ 0 ],
      u * u * p0[ 1 ] + 2 * u * t * c[ 1 ] + t * t * p1[ 1 ],
    ];
  };
  const top: string[] = [];
  const bot: string[] = [];
  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    const [ x, y ] = at( t );
    const [ nx, ny ] = at( Math.min( 1, t + 0.01 ) );
    let dx = nx - x, dy = ny - y;
    const L = Math.hypot( dx, dy ) || 1;
    dx /= L; dy /= L;
    const w = halfW * taper( t );
    top.push( `${ f( x - dy * w ) },${ f( y + dx * w ) }` );
    bot.push( `${ f( x + dy * w ) },${ f( y - dx * w ) }` );
  }
  return `M${ top[ 0 ] } L${ top.slice( 1 ).join( ' L' ) } L${ bot.reverse().join( ' L' ) } Z`;
}

/** Pointed at both ends: a stem lying across the ball. */
const lens = ( t: number ): number => Math.pow( Math.sin( Math.PI * t ), 0.65 );
/** Fat at the base, pointed at the tip: a stem escaping the ball. */
const hookTaper = ( t: number ): number => Math.pow( 1 - t, 0.9 );

function build( seed: number, wraps = 22, hooks = 28 ): { wrap: string; hook: string } {
  const s = seed * 53;
  const R = TUMBLE_R;
  const wrapD: string[] = [];
  const hookD: string[] = [];

  for ( let i = 0; i < wraps; i++ ) {
    const a = h( s + i ) * Math.PI * 2;
    const span = Math.PI * ( 0.55 + h( s + i + 11 ) * 0.6 );
    const r1 = R * ( 0.88 + h( s + i + 23 ) * 0.14 );
    const r2 = R * ( 0.88 + h( s + i + 41 ) * 0.14 );
    const p0: P = [ CX + Math.cos( a ) * r1, CY + Math.sin( a ) * r1 ];
    const p1: P = [ CX + Math.cos( a + span ) * r2, CY + Math.sin( a + span ) * r2 ];
    const mx = ( p0[ 0 ] + p1[ 0 ] ) / 2, my = ( p0[ 1 ] + p1[ 1 ] ) / 2;
    const dx = mx - CX, dy = my - CY;
    const L = Math.hypot( dx, dy ) || 1;
    // Negative pull sends the control point through the centre, so the stem
    // crosses the disc instead of hugging its edge.
    const pull = R * ( h( s + i + 59 ) < 0.42
      ? -( 0.15 + h( s + i + 83 ) * 0.85 )
      : ( 0.55 + h( s + i + 59 ) * 0.5 ) );
    const c: P = [ CX + ( dx / L ) * pull, CY + ( dy / L ) * pull ];
    wrapD.push( sliver( p0, c, p1, 1.1 + h( s + i + 71 ) * 0.9, lens ) );
  }

  for ( let i = 0; i < hooks; i++ ) {
    const a = ( i / hooks ) * Math.PI * 2 + ( h( s + i + 101 ) - 0.5 ) * 0.28;
    const r0 = R * ( 0.68 + h( s + i + 131 ) * 0.2 );
    const r1 = R * ( 1.12 + h( s + i + 157 ) * 0.3 );
    // The tip swings sideways, which is what makes these read as hooked stems
    // rather than as the spines of a chestnut.
    const curl = ( h( s + i + 181 ) - 0.5 ) * 1.15;
    const p0: P = [ CX + Math.cos( a ) * r0, CY + Math.sin( a ) * r0 ];
    const p1: P = [ CX + Math.cos( a + curl ) * r1, CY + Math.sin( a + curl ) * r1 ];
    const mr = ( r0 + r1 ) / 2, ma = a + curl * 0.35;
    const c: P = [ CX + Math.cos( ma ) * mr, CY + Math.sin( ma ) * mr ];
    hookD.push( sliver( p0, c, p1, 0.85 + h( s + i + 199 ) * 0.6, hookTaper ) );
  }

  return { wrap: wrapD.join( ' ' ), hook: hookD.join( ' ' ) };
}

const WEEDS = [ 0, 1, 2, 3 ].map( i => build( i + 1 ) );

/** The stems lying across the body, one set per passcode digit. */
export const TUMBLEWEED_WRAP: string[] = WEEDS.map( w => w.wrap );
/** The stems escaping the rim, one set per digit. Drawn UNDER the body. */
export const TUMBLEWEED_HOOK: string[] = WEEDS.map( w => w.hook );

/** Roraima's yellow, the theme's own Dry Grass — dry straw by name and by hue. */
export const TUMBLE_STRAW = '#DDBE6D';
/** The body under the stems: the same straw taken well down, so the strands read
 *  against it without either tone going dark. */
export const TUMBLE_BODY = '#B08733';
/** ONE outline tone for every unentered dot — the frame never changes per digit
 *  (the rule Aurora's orbs settled). Straw with the colour dried out of it. */
export const TUMBLE_OFF = '#BCAE92';
